/**
 * JS-мост к мобильному SDK (PM 2026-07-24, WebView-режим).
 *
 * Когда веб-пейволл открыт во встроенном WebView SDK (а не во внешнем
 * браузере), страница может слать SDK события напрямую:
 *  - iOS: window.webkit.messageHandlers.web2app.postMessage(obj)
 *  - Android (WEB-812, задел): window.web2appBridge.postMessage(json)
 *
 * Возвращает true, если мост есть и событие отправлено. Во внешнем браузере
 * моста нет → false, страница работает по-старому (URL-схема/кнопка).
 */
interface Web2AppBridgeWindow {
  webkit?: {
    messageHandlers?: {
      web2app?: { postMessage: (m: unknown) => void };
    };
  };
  web2appBridge?: { postMessage: (m: string) => void };
}

/**
 * WEB-853: есть ли мост, т.е. открыта ли страница ВНУТРИ контейнера SDK.
 *
 * Нужно, чтобы кнопка закрытия рисовалась только там, где ей есть кому
 * сообщить о закрытии. В обычном браузере (переход из рекламы, прямая
 * ссылка) закрывать нечего — кнопка была бы мёртвой, поэтому её там нет
 * вовсе, и режим показа ничего не меняет.
 *
 * Проверка ровно та же, что делает postToWeb2AppBridge ниже, только без
 * отправки — держать обе в синхроне.
 */
export function hasWeb2AppBridge(): boolean {
  if (typeof window === 'undefined') return false;
  const w = window as unknown as Web2AppBridgeWindow;
  return Boolean(
    w.webkit?.messageHandlers?.web2app?.postMessage ??
      w.web2appBridge?.postMessage,
  );
}

export function postToWeb2AppBridge(message: Record<string, unknown>): boolean {
  const w = window as unknown as Web2AppBridgeWindow;
  const payload = { source: 'web2app', ...message };
  const ios = w.webkit?.messageHandlers?.web2app;
  if (ios?.postMessage) {
    ios.postMessage(payload);
    return true;
  }
  if (w.web2appBridge?.postMessage) {
    w.web2appBridge.postMessage(JSON.stringify(payload));
    return true;
  }
  return false;
}

/**
 * Исход показа пейвола, который уезжает в контейнер SDK полем `status`
 * события `paywall_result`:
 *   - `success`   — оплата подтверждена (SDK закрывает окно и отдаёт грант);
 *   - `dismissed` — покупатель закрыл пейвол кнопкой, не заплатив (окно НЕ
 *     закрывается этим событием, следом идёт отдельный `close`).
 */
export type PaywallResultStatus = 'success' | 'dismissed';

/**
 * WEB-1143 (хвост П4) — ЕДИНСТВЕННАЯ точка сборки события `paywall_result`.
 *
 * Исход показа в сообщении несёт только поле `status`, и раньше оно
 * дописывалось руками в трёх местах (два success-пути `PublicPaywall` и
 * кнопка закрытия). Потерять его при правке одного из мест было нечем:
 * сообщение осталось бы валидным, событие доехало бы, а приложение молча
 * перестало бы различать «оплатил» и «закрыл» — для `success` это ещё и
 * незакрывающееся окно, потому что терминальность SDK решает по `status`.
 *
 * Форма провода не изменилась (`{source:'web2app', event:'paywall_result',
 * status}`) — старые SDK (0.3.0+) читают ровно то же самое; обратная
 * совместимость обеспечивается тем, что сборка стала одна, а не другой.
 */
export function postPaywallResultToBridge(
  status: PaywallResultStatus,
): boolean {
  return postToWeb2AppBridge({ event: 'paywall_result', status });
}

/**
 * WEB-1143 (П4): ретрансляция события ВОРОНКИ (квиза) в WebView-мост, когда
 * квиз открыт внутри контейнера SDK. Приложение клиента получает поток
 * прохождения (старт / просмотр экрана / ответ / завершение) — чтобы, напр.,
 * пропустить свой онбординг по факту прохождения веб-квиза.
 *
 * Внутренние имена событий аналитики (SCREEN_VIEW, …) маппятся в стабильный
 * контракт моста `quiz_*`. **PII не уходит в мост**: email (EMAIL_SUBMIT) и
 * сырое значение ответа (SCREEN_ANSWER metadata.value) вырезаются — в мост
 * едут только факт события и безопасные метаданные (индекс/тип блока/прогресс).
 * Сами ответы — отдельным контролируемым путём (S2S user/properties, WEB-1140).
 *
 * No-op вне контейнера SDK (моста нет) — возвращает false.
 */
const QUIZ_EVENT_BRIDGE_NAME: Record<string, string> = {
  QUIZ_START: 'quiz_start',
  SCREEN_VIEW: 'quiz_screen_view',
  SCREEN_ANSWER: 'quiz_answer',
  EMAIL_SUBMIT: 'quiz_email_submit',
  QUIZ_COMPLETE: 'quiz_complete',
};

export function relayFunnelEventToBridge(
  event: string,
  payload: Record<string, unknown> = {},
): boolean {
  const bridgeEvent = QUIZ_EVENT_BRIDGE_NAME[event];
  if (!bridgeEvent) return false; // ретранслируем только известный набор
  if (!hasWeb2AppBridge()) return false;

  // Безопасные метаданные: НЕ пропускаем email и сырое значение ответа.
  const raw = (payload.metadata ?? {}) as Record<string, unknown>;
  const safeMeta: Record<string, unknown> = {};
  if (typeof raw.blockId === 'string') safeMeta.blockId = raw.blockId;
  if (typeof raw.blockType === 'string') safeMeta.blockType = raw.blockType;

  const safePayload: Record<string, unknown> = { event: bridgeEvent };
  if (typeof payload.screenId === 'string') safePayload.screenId = payload.screenId;

  // Прогресс прохождения («экран N из M»). Контракт МОСТА — верхнеуровневый:
  // и документация интегратора, и SDK читают `data.screenIndex`. А визард
  // кладёт его в metadata, потому что верхний уровень payload расплющивается
  // в тело POST серверной аналитики, где неизвестное поле отбивает 400 ВСЁ
  // событие (глобальный forbidNonWhitelisted; шрам WEB-933 п.3 про clickIds).
  // Поэтому читаем из обоих мест и отдаём наружу наверх.
  const asNumber = (v: unknown): number | undefined =>
    typeof v === 'number' && Number.isFinite(v) ? v : undefined;
  const screenIndex = asNumber(payload.screenIndex) ?? asNumber(raw.screenIndex);
  const screenTotal = asNumber(payload.screenTotal) ?? asNumber(raw.screenTotal);
  if (screenIndex !== undefined) safePayload.screenIndex = screenIndex;
  if (screenTotal !== undefined) safePayload.screenTotal = screenTotal;

  if (Object.keys(safeMeta).length > 0) safePayload.metadata = safeMeta;

  return postToWeb2AppBridge(safePayload);
}
